/**
 * QuestionBank.tsx  —  W38 mobile repair
 *
 * Browsable, filterable catalog of all M-PACT methodology questions
 * used in the helix decision tree.
 *
 * W38 fixes applied:
 *   1. Header / brand chrome: OperatorShell with pageTitle + pageMeta subtitle
 *      showing Tenant 1 + question count.
 *   2. CP grouping: sticky horizontal CP-filter pill strip; questions grouped
 *      under collapsible CP section headers.
 *   3. Compact cards on mobile: title, type pill, M-PACT pillar pill, single
 *      "View in helix context" CTA. "Coaching + answers" expands inline.
 *      "Use in cadence template" removed entirely.
 *   4. 5-color palette: all black CTA buttons replaced with gray outline style.
 *   5. Breadcrumb: OperatorShell breadcrumbs="Methodology / Question Bank".
 *   6. Search input: sticky under the header, full mobile width.
 *
 * Canon: no exclamation points, no emojis, M-PACT hyphenated, Checkpoint not Gate.
 * Palette: gray / red / orange / yellow / green only on interactive chrome.
 */

import { useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  ALL_QUESTIONS,
  type QuestionDef,
  type IndustryKey,
} from "../../../shared/questions";
import { CP_COACHING } from "@/lib/cp-coaching";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";
import { Search, X, ChevronDown, ChevronUp } from "lucide-react";
import { useLocation } from "wouter";
import { useAccounts } from "@/pages/dashboards/live-data";

// ─── Locked CP names (canon 2026-05-26) ──────────────────────────────────────
const CP_FULL_NAMES: Record<number, string> = {
  1:  "CP1 — Project",
  2:  "CP2 — Need / Pain / Demonstration",
  3:  "CP3 — Budget",
  4:  "CP4 — Timeline",
  5:  "CP5 — Authority — Project / Initiative",
  6:  "CP6 — Technical and Financial Sign-off",
  7:  "CP7 — Org Structure and Influence Map",
  8:  "CP8 — Competitive Position",
  9:  "CP9 — Compliance — IT Internal and Industry-Specific",
  10: "CP10 — Legal — Contracts, SLAs, Terms",
};

const CP_SHORT_NAMES: Record<number, string> = {
  1:  "CP1",
  2:  "CP2",
  3:  "CP3",
  4:  "CP4",
  5:  "CP5",
  6:  "CP6",
  7:  "CP7",
  8:  "CP8",
  9:  "CP9",
  10: "CP10",
};

// ─── M-PACT pillar taxonomy ───────────────────────────────────────────────────
const MPACT_PILLAR_MAP: Record<number, string> = {
  1:  "M",
  2:  "M",
  3:  "P",
  4:  "A",
  5:  "T",
  6:  "T",
  7:  "A",
  8:  "C",
  9:  "C",
  10: "P",
};

const MPACT_PILLARS = [
  { key: "M", label: "Methodology" },
  { key: "P", label: "Process / Prioritization" },
  { key: "A", label: "Accountability" },
  { key: "C", label: "Cadence / Coaching" },
  { key: "T", label: "Timing / Timeline management" },
];

// ─── Answer type inference ────────────────────────────────────────────────────
function inferAnswerType(q: QuestionDef): string {
  const opts = q.options;
  if (opts.length === 0) return "Free text";
  if (opts.length === 2) return "Yes / No";
  const labels = opts.map((o) => o.label.toLowerCase()).join(" ");
  if (labels.includes("scale") || labels.includes("1–") || labels.includes("1-")) return "Scale";
  if (opts.length <= 3) return "Yes / No";
  return "Multi-select";
}

// ─── Coaching prompt from CP_COACHING ─────────────────────────────────────────
function coachingPrompt(q: QuestionDef): string {
  const cp = CP_COACHING[q.cp];
  if (!cp) return "Use this question to validate the checkpoint and advance the opportunity.";
  return cp.inConversation;
}

// ─── Pillar pill colors (palette: gray/green/orange/amber only) ───────────────
const PILLAR_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  M: { bg: "#FAFAF7", text: "#0B0B0F",  border: "#E5E5E7" },
  P: { bg: "#F0FDF4", text: "#15803D",  border: "#BBF7D0" },
  A: { bg: "#FFF7ED", text: "#C2410C",  border: "#FED7AA" },
  C: { bg: "#FAFAF7", text: "#0B0B0F",  border: "#E5E5E7" },
  T: { bg: "#FFFBEB", text: "#B45309",  border: "#FDE68A" },
};

// ─── Question Card (compact, mobile-first) ────────────────────────────────────
function QuestionCard({ q, onAnswerOnAccount }: { q: QuestionDef; onAnswerOnAccount: (q: QuestionDef) => void }) {
  const [expanded, setExpanded] = useState(false);

  const pillarKey   = MPACT_PILLAR_MAP[q.cp] ?? "M";
  const pillarColors = PILLAR_COLORS[pillarKey] ?? PILLAR_COLORS.M;
  const pillarInfo  = MPACT_PILLARS.find((p) => p.key === pillarKey);
  const answerType  = inferAnswerType(q);

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: "14px 16px",
        transition: "box-shadow 0.12s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 12px rgba(15,23,42,0.08)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      {/* Pill row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        {/* M-PACT pillar */}
        <span
          style={{
            flexShrink: 0,
            background: pillarColors.bg,
            color: pillarColors.text,
            border: `1px solid ${pillarColors.border}`,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.05em",
            padding: "3px 9px",
            borderRadius: 9999,
            whiteSpace: "nowrap",
          }}
        >
          {pillarKey} — {pillarInfo?.label}
        </span>

        {/* Answer type */}
        <span
          style={{
            flexShrink: 0,
            background: "#FAFAF7",
            color: "#6B6B70",
            border: "1px solid #E5E5E7",
            fontSize: 10,
            fontWeight: 600,
            padding: "3px 9px",
            borderRadius: 9999,
            whiteSpace: "nowrap",
          }}
        >
          {answerType}
        </span>
      </div>

      {/* Question text */}
      <p
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "#0B0B0F",
          margin: "0 0 10px 0",
          lineHeight: 1.5,
        }}
      >
        {q.label}
      </p>

      {/* Expand coaching / options inline */}
      <button
        onClick={() => setExpanded((e) => !e)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 11,
          fontWeight: 700,
          color: "#6B6B70",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "2px 0",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          marginBottom: expanded ? 10 : 0,
        }}
      >
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {expanded ? "Hide detail" : "Coaching + answers"}
      </button>

      {expanded && (
        <div style={{ borderTop: "1px solid #EFEFEE", paddingTop: 12 }}>
          {/* Coaching prompt */}
          <p
            style={{
              fontSize: 12,
              color: "#3F3F44",
              lineHeight: 1.6,
              margin: "0 0 10px 0",
              background: "#FAFAF7",
              border: "1px solid #E5E5E7",
              borderRadius: 6,
              padding: "8px 12px",
            }}
          >
            <span style={{ fontWeight: 700, color: "#0B0B0F" }}>Coaching: </span>
            {coachingPrompt(q)}
          </p>

          {/* Answer options */}
          {q.options.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
              {q.options.map((opt) => {
                const flagColors: Record<string, { bg: string; text: string }> = {
                  OK:        { bg: "#F0FDF4", text: "#15803D" },
                  AMBER:     { bg: "#FFFBEB", text: "#B45309" },
                  SOFT_STOP: { bg: "#FFF7ED", text: "#C2410C" },
                  HARD_STOP: { bg: "#FEF2F2", text: "#DC2626" },
                };
                const fc = flagColors[opt.flag] ?? flagColors.OK;
                return (
                  <div
                    key={opt.key}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "5px 8px",
                      borderRadius: 5,
                      background: fc.bg,
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 700,
                        color: fc.text,
                        marginTop: 1,
                        minWidth: 60,
                      }}
                    >
                      {opt.flag.replace("_", " ")} ({opt.points}pt)
                    </span>
                    <span style={{ fontSize: 12, color: "#3F3F44", lineHeight: 1.4 }}>
                      {opt.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Single CTA — gray outline, palette-clean */}
      <div style={{ marginTop: 12 }}>
        <button
          onClick={() => onAnswerOnAccount(q)}
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#3F3F44",
            background: "#FFFFFF",
            border: "1px solid #D1D5DB",
            borderRadius: 6,
            padding: "6px 14px",
            cursor: "pointer",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Answer on Account
        </button>
      </div>
    </div>
  );
}

// ─── CP section header ────────────────────────────────────────────────────────
function CpSectionHeader({
  cp,
  count,
  open,
  onToggle,
}: {
  cp: number;
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        background: "#FAFAF7",
        border: "1px solid #E5E5E7",
        borderRadius: 8,
        padding: "10px 16px",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "#0B0B0F",
            color: "#FFFFFF",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {cp}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F", letterSpacing: "-0.01em" }}>
          {CP_FULL_NAMES[cp] ?? `CP${cp}`}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#6B6B70",
            background: "#EFEFEE",
            borderRadius: 9999,
            padding: "2px 8px",
          }}
        >
          {count}
        </span>
        {open ? (
          <ChevronUp size={15} color="#6B6B70" />
        ) : (
          <ChevronDown size={15} color="#6B6B70" />
        )}
      </div>
    </button>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
// ─── Account Picker Modal ────────────────────────────────────────────────────
function AccountPickerModal({
  question,
  accounts,
  accountsLoading,
  onClose,
  onSelect,
}: {
  question: QuestionDef;
  accounts: { id: number; name: string }[] | null;
  accountsLoading: boolean;
  onClose: () => void;
  onSelect: (accountId: number) => void;
}) {
  const [accountSearch, setAccountSearch] = useState("");
  const filtered = (accounts ?? []).filter((a) =>
    a.name.toLowerCase().includes(accountSearch.toLowerCase())
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(11,11,15,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 12,
          padding: "24px 24px 20px",
          width: "100%",
          maxWidth: 420,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(11,11,15,0.18)",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#6B6B70", textTransform: "uppercase", marginBottom: 4 }}>
            Answer on Account
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0B0B0F", lineHeight: 1.4 }}>
            {question.label}
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <input
            autoFocus
            type="text"
            placeholder="Search accounts..."
            value={accountSearch}
            onChange={(e) => setAccountSearch(e.target.value)}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "8px 12px",
              fontSize: 13,
              border: "1px solid #E5E5E7",
              borderRadius: 7,
              background: "#FAFAF7",
              color: "#0B0B0F",
              outline: "none",
            }}
          />
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {accountsLoading && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#8E8E94", fontSize: 13 }}>
              Loading accounts...
            </div>
          )}
          {!accountsLoading && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#8E8E94", fontSize: 13 }}>
              No accounts found.
            </div>
          )}
          {filtered.map((acct) => (
            <button
              key={acct.id}
              onClick={() => onSelect(acct.id)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "9px 12px",
                fontSize: 13,
                fontWeight: 500,
                color: "#0B0B0F",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                marginBottom: 2,
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F4F4F5")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {acct.name}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #EFEFEE", textAlign: "right" }}>
          <button
            onClick={onClose}
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#6B6B70",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px 8px",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuestionBank() {
  const [cpFilter, setCpFilter] = useState<number | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [collapsedCps, setCollapsedCps] = useState<Set<number>>(new Set());
  const [pickerQuestion, setPickerQuestion] = useState<QuestionDef | null>(null);
  const [, navigate] = useLocation();
  const { data: accounts, loading: accountsLoading } = useAccounts();

  function handleAnswerOnAccount(q: QuestionDef) {
    setPickerQuestion(q);
  }

  function handleAccountSelect(accountId: number) {
    if (!pickerQuestion) return;
    navigate(`/account/${accountId}/answer?cp=${pickerQuestion.cp}&q=${encodeURIComponent(pickerQuestion.key)}`);
    setPickerQuestion(null);
  }

  // Filter questions
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ALL_QUESTIONS.filter((q) => {
      if (cpFilter !== "ALL" && q.cp !== cpFilter) return false;
      if (term) {
        const hay = (
          q.label +
          " " +
          (q.helpText ?? "") +
          " " +
          q.options.map((o) => o.label).join(" ")
        ).toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [cpFilter, search]);

  // Group filtered questions by CP (1-10 only, exclude pre-qualify)
  const groupedByCp = useMemo(() => {
    const groups: Map<number, QuestionDef[]> = new Map();
    for (let i = 1; i <= 10; i++) groups.set(i, []);
    for (const q of filtered) {
      if (q.cp >= 1 && q.cp <= 10) {
        groups.get(Math.round(q.cp))?.push(q);
      }
    }
    return groups;
  }, [filtered]);

  const totalCount   = ALL_QUESTIONS.filter((q) => q.cp >= 1 && q.cp <= 10).length;
  const shownCount   = filtered.filter((q) => q.cp >= 1 && q.cp <= 10).length;

  function toggleCp(cp: number) {
    setCollapsedCps((prev) => {
      const next = new Set(prev);
      if (next.has(cp)) {
        next.delete(cp);
      } else {
        next.add(cp);
      }
      return next;
    });
  }

  // CP filter pills — All + CP1…CP10
  const cpPills = [
    { value: "ALL" as const, label: "All" },
    ...Array.from({ length: 10 }, (_, i) => ({
      value: (i + 1) as number,
      label: CP_SHORT_NAMES[i + 1],
    })),
  ];

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Methodology", href: "/methodology" },
        { label: "Question Bank" },
      ]}
      pageTitle="Question Bank"
      pageMeta={`${totalCount} questions across CP1–CP10`}
    >
      {/* ── Sticky top controls (search + CP filter pills) ─────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "#FFFFFF",
          paddingBottom: 12,
          marginBottom: 4,
          borderBottom: "1px solid #EFEFEE",
        }}
      >
        {/* Search */}
        <div style={{ position: "relative", marginBottom: 10 }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#8E8E94",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="Search questions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              boxSizing: "border-box",
              paddingLeft: 36,
              paddingRight: search ? 36 : 12,
              paddingTop: 9,
              paddingBottom: 9,
              fontSize: 14,
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              background: "#FAFAF7",
              color: "#0B0B0F",
              outline: "none",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#8E8E94",
                padding: 0,
                display: "flex",
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* CP filter pill strip — horizontally scrollable on mobile */}
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            paddingBottom: 2,
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          {cpPills.map((pill) => {
            const active =
              pill.value === "ALL"
                ? cpFilter === "ALL"
                : cpFilter === pill.value;
            return (
              <button
                key={String(pill.value)}
                onClick={() => setCpFilter(pill.value)}
                style={{
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "5px 12px",
                  borderRadius: 9999,
                  border: active ? "1px solid #0B0B0F" : "1px solid #E5E5E7",
                  background: active ? "#0B0B0F" : "#FFFFFF",
                  color: active ? "#FFFFFF" : "#3F3F44",
                  cursor: "pointer",
                  letterSpacing: "0.02em",
                  whiteSpace: "nowrap",
                  transition: "background 0.1s, color 0.1s",
                }}
              >
                {pill.label}
              </button>
            );
          })}
        </div>

        {/* Count */}
        <div
          style={{
            fontSize: 12,
            color: "#8E8E94",
            marginTop: 8,
          }}
        >
          {shownCount === totalCount
            ? `${totalCount} questions`
            : `${shownCount} of ${totalCount} questions`}
        </div>
      </div>

      {/* ── Question groups ───────────────────────────────────────────────────── */}
      {shownCount === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 24px",
            color: "#8E8E94",
            fontSize: 14,
          }}
        >
          No questions match the current filters. Try broadening the selection.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((cp) => {
            const questions = groupedByCp.get(cp) ?? [];
            if (questions.length === 0) return null;
            const isOpen = !collapsedCps.has(cp);
            return (
              <div key={cp}>
                <CpSectionHeader
                  cp={cp}
                  count={questions.length}
                  open={isOpen}
                  onToggle={() => toggleCp(cp)}
                />
                {isOpen && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    {questions.map((q) => (
                      <QuestionCard key={q.key} q={q} onAnswerOnAccount={handleAnswerOnAccount} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Account picker modal — shown when user clicks "Answer on Account" */}
      {pickerQuestion && (
        <AccountPickerModal
          question={pickerQuestion}
          accounts={accounts}
          accountsLoading={accountsLoading}
          onClose={() => setPickerQuestion(null)}
          onSelect={handleAccountSelect}
        />
      )}
    </OperatorShell>
  );
}
